// Small "Last refreshed" stamp shown near a section header.
// Re-renders every 30 seconds so relative text stays fresh.
//
// With `freshness` (from useSectionData's heartbeat — { builtAt, refreshing,
// active }) it reports the age of the SHARED SNAPSHOT behind the page instead of
// when this browser last fetched: "Data as of 10:42 AM", plus "Refreshing…" while a
// newer copy is being built, or a note when the section isn't being refreshed
// (hidden or its period closed). Without it, the old "Last refreshed" text.
import { useEffect, useState } from 'react';
import './LastRefreshed.css';

function relative(fromDate, now) {
  const ms = now - fromDate;
  if (ms < 45_000)   return 'just now';
  if (ms < 90_000)   return '1 min ago';
  const mins = Math.round(ms / 60_000);
  if (mins < 60)     return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24)      return `${hrs} hr ago`;
  return fromDate.toLocaleDateString();
}

function asOf(date, now) {
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function LastRefreshed({ at, freshness = null, onRefresh }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!at) return undefined;
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, [at]);

  if (!at) return null;
  const builtAt = freshness && freshness.builtAt ? new Date(freshness.builtAt) : null;
  const inactive = !!(freshness && freshness.active === false);
  const refreshing = !!(freshness && freshness.refreshing) && !inactive;

  return (
    <span
      className={`last-refreshed${inactive ? ' last-refreshed--inactive' : ''}`}
      title={builtAt
        ? `Source data built ${builtAt.toLocaleString()} · page updated ${at.toLocaleString()}`
        : at.toLocaleString()}
    >
      <i className={refreshing ? 'fas fa-sync-alt fa-spin' : 'fas fa-clock'} />
      {builtAt ? (
        <span>
          Data as of {asOf(builtAt, now)}
          {refreshing && ' · Refreshing…'}
          {inactive && ' — not refreshed while this section is inactive'}
        </span>
      ) : (
        <span>Last refreshed {relative(at, now)}</span>
      )}
      {onRefresh && (
        <button
          className="last-refreshed-btn"
          onClick={onRefresh}
          title="Refresh now"
        >
          <i className="fas fa-sync-alt" />
        </button>
      )}
    </span>
  );
}
